import React from 'react'
import { useTranslation } from 'react-i18next'
import styles from './errorScreen.module.scss'
import { MESSAGE_SCREENS, MessageScreen } from 'constants/message-screens'
import UserMessageScreen, { Props as UserMessageScreenProps } from './UserMessageScreen'
import regLogger from 'modules/reg-logger'
import { LogEntry } from 'constants/global'
import { copyToClipboard } from 'utils'
import useElement from 'hooks/use-element'
import { ReactComponent as CopyIcon } from 'images/icons/icn_copy.svg'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducers'

const RegistrationLog = ({ logs }: { logs: LogEntry[] }) => {
  const formatLog = ({ start, end, operation, ctx, error, rayId }: LogEntry) =>
    `${start}; ${end}; ${operation}; ${ctx}; ${error}; ${rayId}`

  if (!logs.length) return <p aria-hidden>--</p>

  return (
    <>
      {logs.map(l => (
        <p key={`${l.operation}-${l.start}`} className={styles.log}>
          {formatLog(l)}
        </p>
      ))}
    </>
  )
}

interface ErrorInfoProps {
  message: string
  addRegLog?: boolean
}

const ErrorInfo = ({ message, addRegLog }: ErrorInfoProps) => {
  const { t: tUI } = useTranslation('strCommonUI')
  const { t: tMsg } = useTranslation('strMessageScreen')

  const lastKnownRayId = useSelector((state: AppState) => state.user.storedRayId)
  const logs = addRegLog ? regLogger.getLogsByDate() : null
  const [infoContainer, infoContainerRef] = useElement<HTMLDivElement>()

  const handleClick = () => {
    const errorInfo = infoContainer?.innerText
    if (errorInfo) copyToClipboard(errorInfo)
  }

  const location = window.location.href

  return (
    <>
      <button
        className={styles.copyBtn}
        onClick={handleClick}
        title={tUI('button.copyToClipboard')}
        aria-label={tUI('button.copyToClipboard')}
      >
        <CopyIcon aria-hidden />
      </button>
      <div ref={infoContainerRef} className={styles.infoContainer}>
        <p>{tMsg('errorInfo.intro')}</p>
        <p>{message}</p>
        <p>{tMsg('errorInfo.location')}</p>
        <p>{location}</p>
        {lastKnownRayId && <p>{`ID: ${lastKnownRayId}`}</p>}
        {logs && (
          <>
            <br />
            <p>{tMsg('errorInfo.rlog')}</p>
            <RegistrationLog logs={logs} />
          </>
        )}
      </div>
    </>
  )
}

export interface ErrorScreenProps extends Omit<UserMessageScreenProps, 'moreInfo'> {
  type: MessageScreen
  message: string
  disableRegLog?: boolean
}

const ErrorScreen = ({ type, message, disableRegLog = false, ...rest }: ErrorScreenProps) => {
  const { t } = useTranslation('strCommonUI')

  return (
    <UserMessageScreen
      {...MESSAGE_SCREENS[type]}
      {...rest}
      moreInfo={{
        toggleText: t('button.seeError'),
        text: <ErrorInfo message={message} addRegLog={!disableRegLog} />,
      }}
    />
  )
}

export default ErrorScreen
